import { TaskCard } from "@/components/tasks/TaskCard";
import {
  type ActionItem,
  type ActionState,
} from "@/hooks/use-action-items";

/**
 * BW19 PR-E — 4-column kanban for action items.
 *
 * Drag handlers wire to `onMove(id, state)`. We keep this simple and
 * pointer-driven: a single dataTransfer payload, no library.
 */

const COLUMNS: { state: ActionState; label: string }[] = [
  { state: "open", label: "Open" },
  { state: "in_progress", label: "In progress" },
  { state: "blocked", label: "Blocked" },
  { state: "done", label: "Done" },
];

export function TaskKanbanView({
  items,
  onCycleState,
  onCancel,
  onMove,
  onOpen,
}: {
  items: ActionItem[];
  onCycleState: (id: string, next: ActionState) => void;
  onCancel?: (id: string) => void;
  onMove: (id: string, state: ActionState) => void;
  onOpen: (id: string) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(220px, 1fr))`,
        gap: "var(--space-3)",
      }}
    >
      {COLUMNS.map((col) => {
        const colItems = items.filter((it) => it.state === col.state);
        return (
          <div
            key={col.state}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData("text/plain");
              if (id) onMove(id, col.state);
            }}
            style={{
              background: "var(--bg)",
              border: "1px solid var(--hair)",
              borderRadius: "var(--radius-card)",
              padding: "var(--space-3)",
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-2)",
              minHeight: 200,
            }}
          >
            <header
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "var(--fg-2)",
                fontSize: 12,
              }}
            >
              <span>{col.label}</span>
              <span>{colItems.length}</span>
            </header>
            {colItems.map((item) => (
              <div
                key={item.id}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("text/plain", item.id)}
              >
                <TaskCard
                  item={item}
                  onCycleState={(next) => onCycleState(item.id, next)}
                  onCancel={onCancel ? () => onCancel(item.id) : undefined}
                  onOpen={() => onOpen(item.id)}
                />
              </div>
            ))}
            {colItems.length === 0 && (
              <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 12 }}>
                No tasks here.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
